// apps/web/src/routes/(showcase)/showcase/operator.tsx
import { Tooltip, TooltipContent, TooltipTrigger } from "@acme/ui/tooltip";
import { createFileRoute } from "@tanstack/react-router";
import { Info } from "lucide-react";
import type { ReactNode } from "react";
import { AnimatedCounter } from "~/components/showcase/motion/animated-counter";
import { ScrollReveal } from "~/components/showcase/motion/scroll-reveal";
import { AddonsTable } from "~/components/showcase/operator/addons-table";
import { ChannelDonut } from "~/components/showcase/operator/channel-donut";
import { OccupancyGauge } from "~/components/showcase/operator/occupancy-gauge";
import { RevenueSparkline } from "~/components/showcase/operator/revenue-sparkline";
import { GoldRule } from "~/components/showcase/ornaments/gold-rule";
import { seo } from "~/lib/seo";
import { OPERATOR_METRICS } from "~/lib/showcase/operator-data";
import { useSkin } from "~/lib/showcase/skin";
import {
  generateStructuredDataGraph,
  getShowcasePageSchemas,
} from "~/lib/structured-data";
import * as m from "~/paraglide/messages.js";

export const Route = createFileRoute("/(showcase)/showcase/operator")({
  component: OperatorPage,
  head: () => {
    const title = m.meta_showcase_operator_title();
    const description = m.meta_showcase_operator_description();
    const seoData = seo({
      // The hospitality booking demo no longer matches what Lindevo sells.
      // Kept working and crawlable, but out of the index until it is ported
      // out to apps/mockup. Also removed from sitemap.xml.
      noindex: true,
      description,
      path: "/showcase/operator",
      title,
    });
    const structuredData = generateStructuredDataGraph(
      getShowcasePageSchemas({
        description,
        pageName: title,
        path: "/showcase/operator",
      })
    );
    return {
      links: seoData.links,
      meta: seoData.meta,
      scripts: [{ children: structuredData, type: "application/ld+json" }],
    };
  },
});

function OperatorPage() {
  const metrics = OPERATOR_METRICS;
  const { skin } = useSkin();
  const directPct = Math.round(metrics.channelMix.direct);
  return (
    <div className="mx-auto max-w-6xl px-6 py-12">
      <header className="mb-10">
        {skin === "heritage" ? (
          <>
            <p className="sc-eyebrow flex items-center gap-3">
              <span
                aria-hidden="true"
                className="h-px w-10"
                style={{ backgroundColor: "var(--sc-rule)" }}
              />
              {m.showcase_operator_eyebrow()}
            </p>
            <h1
              className="sc-display mt-4 text-5xl text-[var(--sc-fg)] leading-[0.95] sm:text-7xl"
              style={{ fontStyle: "italic", fontWeight: 700 }}
            >
              {m.showcase_operator_title()}
            </h1>
            <GoldRule className="mt-6 max-w-md" variant="diamond" />
            <p className="sc-body mt-6 max-w-2xl text-[var(--sc-fg)] text-lg leading-relaxed">
              {m.showcase_operator_lead()}
            </p>
          </>
        ) : (
          <>
            <p className="sc-eyebrow flex items-center gap-3 text-[var(--sc-muted)]">
              <span
                aria-hidden="true"
                className="h-px w-10 bg-[var(--sc-accent)]"
              />
              {m.showcase_operator_eyebrow()}
            </p>
            <h1
              className="sc-display mt-4 text-4xl text-[var(--sc-fg)] leading-[1.05] sm:text-6xl"
              style={{ letterSpacing: "var(--sc-tracking-display)" }}
            >
              {m.showcase_operator_title()}
            </h1>
            <p className="mt-4 max-w-2xl text-[var(--sc-muted)] text-lg">
              {m.showcase_operator_lead()}
            </p>
          </>
        )}
      </header>

      {/* KPI strip — animated counters: the data wow moment */}
      <ScrollReveal>
        <div
          className="mb-10 grid grid-cols-2 gap-px border bg-[var(--sc-border)] sm:grid-cols-4"
          style={{
            borderColor: "var(--sc-border)",
            borderRadius: "var(--sc-radius)",
            boxShadow: "var(--sc-shadow)",
            overflow: "hidden",
          }}
        >
          <KpiTile
            label={m.showcase_operator_occupancy_label()}
            suffix="%"
            to={metrics.occupancyPct}
          />
          <KpiTile
            label={m.showcase_operator_revenue_30d()}
            prefix="€"
            to={metrics.revenueTotalEur}
          />
          <KpiTile
            explainer={m.showcase_kpi_adr_explainer()}
            label={m.showcase_index_kpi_adr()}
            prefix="€"
            to={metrics.averageDailyRateEur}
          />
          <KpiTile
            label={m.showcase_operator_channels_label()}
            sublabel={m.showcase_operator_channel_direct()}
            suffix="%"
            to={directPct}
          />
        </div>
      </ScrollReveal>

      <div
        className={
          skin === "heritage"
            ? "grid gap-6 sm:grid-cols-2"
            : "grid gap-6 sm:grid-cols-6"
        }
      >
        <Card span={skin === "heritage" ? 1 : 2}>
          <CardTitle>{m.showcase_operator_occupancy_label()}</CardTitle>
          <OccupancyGauge pct={metrics.occupancyPct} />
        </Card>
        <Card span={skin === "heritage" ? 1 : 4}>
          <CardTitle>{m.showcase_operator_revenue_30d()}</CardTitle>
          <RevenueSparkline
            ratePerDayEur={metrics.averageDailyRateEur}
            series={metrics.revenueByDay}
            totalEur={metrics.revenueTotalEur}
          />
        </Card>
        <Card span={skin === "heritage" ? 1 : 3}>
          <CardTitle>{m.showcase_operator_channels_label()}</CardTitle>
          <ChannelDonut mix={metrics.channelMix} />
        </Card>
        <Card span={skin === "heritage" ? 1 : 3}>
          <CardTitle>{m.showcase_operator_addons_title()}</CardTitle>
          <AddonsTable addons={metrics.topAddons} />
        </Card>
      </div>
    </div>
  );
}

interface KpiTileProps {
  explainer?: string;
  label: string;
  prefix?: string;
  sublabel?: string;
  suffix?: string;
  to: number;
}

function KpiTile({
  explainer,
  label,
  prefix = "",
  sublabel,
  suffix = "",
  to,
}: KpiTileProps) {
  return (
    <div className="bg-[var(--sc-surface)] p-6 sm:p-8">
      <div className="sc-eyebrow flex items-center gap-1">
        {label}
        {explainer ? (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={explainer}
                className="inline-flex items-center text-[var(--sc-muted)] hover:text-[var(--sc-fg)]"
                type="button"
              >
                <Info aria-hidden="true" className="size-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent className="max-w-[220px] bg-[var(--sc-fg)] text-[var(--sc-bg)]">
              {explainer}
            </TooltipContent>
          </Tooltip>
        ) : null}
      </div>
      <div
        className="mt-2 flex items-baseline gap-2 font-[family-name:var(--sc-font-display)] text-4xl text-[var(--sc-fg)] sm:text-5xl"
        style={{ letterSpacing: "var(--sc-tracking-display)" }}
      >
        <AnimatedCounter
          className="sc-num-display"
          format={(n) => Math.round(n).toLocaleString()}
          prefix={prefix}
          suffix={suffix}
          to={to}
        />
      </div>
      {sublabel ? (
        <div className="mt-1 text-[var(--sc-muted)] text-xs">{sublabel}</div>
      ) : null}
    </div>
  );
}

interface CardProps {
  children: ReactNode;
  span?: number;
}

const COL_SPAN: Record<number, string> = {
  1: "",
  2: "sm:col-span-2",
  3: "sm:col-span-3",
  4: "sm:col-span-4",
};

function Card({ children, span = 1 }: CardProps) {
  const colSpan = COL_SPAN[span] ?? "";
  return (
    <ScrollReveal className={colSpan}>
      <section
        className="h-full border bg-[var(--sc-surface)] p-6"
        style={{
          borderColor: "var(--sc-border)",
          borderRadius: "var(--sc-radius)",
          boxShadow: "var(--sc-shadow)",
        }}
      >
        {children}
      </section>
    </ScrollReveal>
  );
}

function CardTitle({ children }: { children: ReactNode }) {
  return (
    <>
      <h2
        className="mb-3 font-[family-name:var(--sc-font-display)] text-[var(--sc-fg)] text-lg"
        style={{ letterSpacing: "var(--sc-tracking-display)" }}
      >
        {children}
      </h2>
      <GoldRule className="mb-5" variant="thin" />
    </>
  );
}
